import {
  BusinessLineCard,
  BusinessLineId,
  GlobalBalance,
  GlobalBalancePart,
  GlobalBalancePartStatus,
  GlobalBalanceStatus,
  GlobalDashboardSummary,
  GlobalIncomeExpense,
  GlobalRecentActivity,
  GlobalServiceId,
  GlobalServiceSeries,
  ServiceUsageSlice,
} from '@app/domain/models/global-dashboard.model';
import {
  BusinessLineCardDto,
  GlobalBalanceDto,
  GlobalBalancePartDto,
  GlobalDashboardSummaryDto,
  GlobalIncomeExpenseDto,
  GlobalRecentActivityDto,
  GlobalServiceSeriesDto,
  ServiceUsageSliceDto,
} from '@app/data/dtos/global-dashboard.dto';
import type {
  PaymentsDashboardData,
  PaymentsSeriePoint,
} from '@app/domain/models/payments-dashboard/payments-dashboard.model';
import { sumarMontos } from '@app/domain/models/payments-dashboard/payments-dashboard-rules';

/**
 * Ids de servicio que el front conoce. Cualquier servicio desconocido que mande
 * el backend se descarta silenciosamente: así una línea de negocio nueva no
 * rompe el dashboard viejo, y el layout solo pinta lo que puede etiquetar.
 *
 * 'payments' NO va aquí a propósito: lo fusiona el front. Si algún día el back lo
 * mandara en global-summary, se descartaría y no se contaría dos veces.
 */
const KNOWN_SERVICES = new Set<GlobalServiceId>([
  'transfers',
  'dispersions',
  'tpv',
  'cards',
  'polipay_qr',
  'payment_links',
  'reference_payments',
]);

const KNOWN_LINES = new Set<BusinessLineId>(['spei', 'tpv', 'cards']);

const DEFAULT_CURRENCY = 'MXN';

function toServiceId(value: string | undefined): GlobalServiceId | null {
  const normalized = (value ?? '').toString().trim().toLowerCase() as GlobalServiceId;
  return KNOWN_SERVICES.has(normalized) ? normalized : null;
}

function toLineId(value: string | undefined): BusinessLineId | null {
  const normalized = (value ?? '').toString().trim().toLowerCase() as BusinessLineId;
  return KNOWN_LINES.has(normalized) ? normalized : null;
}

function mapSeries(dtos: GlobalServiceSeriesDto[] | undefined): GlobalServiceSeries[] {
  return (dtos ?? []).reduce<GlobalServiceSeries[]>((acc, dto) => {
    const serviceId = toServiceId(dto?.service);
    if (serviceId) {
      acc.push({
        serviceId,
        points: (dto.points ?? []).map((p) => ({ bucket: p.bucket, value: Number(p.value) || 0 })),
      });
    }
    return acc;
  }, []);
}

function mapIncomeExpense(dtos: GlobalIncomeExpenseDto[] | undefined): GlobalIncomeExpense[] {
  return (dtos ?? []).reduce<GlobalIncomeExpense[]>((acc, dto) => {
    const serviceId = toServiceId(dto?.service);
    if (serviceId) {
      acc.push({
        serviceId,
        ingresos: Number(dto.ingresos) || 0,
        egresos: Number(dto.egresos) || 0,
      });
    }
    return acc;
  }, []);
}

function mapLineCards(dtos: BusinessLineCardDto[] | undefined, currency: string): BusinessLineCard[] {
  return (dtos ?? []).reduce<BusinessLineCard[]>((acc, dto) => {
    const lineId = toLineId(dto?.line);
    if (lineId) {
      acc.push({
        lineId,
        ingresosCount: Number(dto.ingresos_count) || 0,
        ingresosAmount: Number(dto.ingresos_amount) || 0,
        egresosCount: Number(dto.egresos_count) || 0,
        egresosAmount: Number(dto.egresos_amount) || 0,
        currency: dto.currency || currency,
      });
    }
    return acc;
  }, []);
}

function mapServiceUsage(dtos: ServiceUsageSliceDto[] | undefined): ServiceUsageSlice[] {
  return (dtos ?? []).reduce<ServiceUsageSlice[]>((acc, dto) => {
    const serviceId = toServiceId(dto?.service);
    if (serviceId) {
      acc.push({
        serviceId,
        count: Number(dto.count) || 0,
        percentage: Number(dto.percentage) || 0,
      });
    }
    return acc;
  }, []);
}

function mapRecentActivity(
  dtos: GlobalRecentActivityDto[] | undefined,
  currency: string
): GlobalRecentActivity[] {
  return (dtos ?? []).reduce<GlobalRecentActivity[]>((acc, dto) => {
    const serviceId = toServiceId(dto?.service);
    if (serviceId) {
      acc.push({
        id: String(dto.id ?? ''),
        serviceId,
        direction: dto.direction === 'OUT' ? 'OUT' : 'IN',
        amount: Number(dto.amount) || 0,
        currency: dto.currency || currency,
        ordenante: dto.ordenante ?? '',
        beneficiario: dto.beneficiario ?? '',
        realizador: dto.realizador ?? '',
        clientName: dto.client_name ?? '',
        reference: dto.reference ?? '',
        occurredAt: dto.occurred_at ?? '',
        relativeDate: dto.relative_date ?? '',
      });
    }
    return acc;
  }, []);
}

// ── Saldo global (feature 108): lo calcula banca, el front solo lo valida ──────

const BALANCE_STATUSES = new Set<GlobalBalanceStatus>(['complete', 'partial', 'absent']);
const PART_STATUSES = new Set<GlobalBalancePartStatus>(['included', 'absent', 'not_applicable']);

/** Cadena decimal plana ("1234.56", "-0.10"): sin exponente, sin espacios internos. */
const DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * Un importe del back es DATO solo si es un número finito o una cadena decimal no
 * vacía. `null`, `""`, `"NaN"`, `"1e999"` o cualquier otra cosa ⇒ `null`
 * (ausente). Prohibido `Number(x) || 0`: `Number(null)` y `Number("")` son 0, y un
 * cero en una pantalla de dinero es una afirmación.
 */
export function decimalOrNull(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!DECIMAL.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** Estado por lista cerrada; lo desconocido cae al más conservador (`absent`). */
function balanceStatusOf(raw: unknown): GlobalBalanceStatus {
  const value = typeof raw === 'string' ? (raw.trim().toLowerCase() as GlobalBalanceStatus) : null;
  return value !== null && BALANCE_STATUSES.has(value) ? value : 'absent';
}

function partStatusOf(raw: unknown): GlobalBalancePartStatus {
  const value = typeof raw === 'string' ? (raw.trim().toLowerCase() as GlobalBalancePartStatus) : null;
  return value !== null && PART_STATUSES.has(value) ? value : 'absent';
}

const ABSENT_PART: GlobalBalancePart = { amount: null, status: 'absent' };

function mapBalancePart(dto: GlobalBalancePartDto | null | undefined): GlobalBalancePart {
  if (!dto || typeof dto !== 'object') return ABSENT_PART;
  const status = partStatusOf(dto.status);
  if (status === 'not_applicable') return { amount: null, status };
  const amount = decimalOrNull(dto.amount);
  // Incoherente ("incluida" sin importe legible) ⇒ ausente: falla cerrada.
  if (status === 'included' && amount === null) return ABSENT_PART;
  return { amount: status === 'absent' ? null : amount, status };
}

/**
 * Saldo global del back. Sin `balance` (banca anterior al contrato C4) ⇒ todo
 * ausente: la pantalla dice "No disponible" y NO pinta el `global_balance` legado.
 * Un estado incoherente con el importe falla cerrado (ausente).
 */
export function mapGlobalBalance(dto: GlobalBalanceDto | null | undefined): GlobalBalance {
  if (!dto || typeof dto !== 'object') {
    return { total: null, status: 'absent', spei: ABSENT_PART, cards: ABSENT_PART };
  }
  const spei = mapBalancePart(dto.spei);
  const cards = mapBalancePart(dto.cards);
  const total = decimalOrNull(dto.total);
  const status = balanceStatusOf(dto.status);
  if (status === 'absent' || total === null) {
    return { total: null, status: 'absent', spei, cards };
  }
  return { total, status, spei, cards };
}

/** Formato de un bucket, tal como lo manda cada back (ver `chart-period-label.util`). */
type FormatoDeBucket = 'hora' | 'dia' | 'semana' | 'mes' | 'anio';

const RE_HORA = /^(\d{4}-\d{2}-\d{2})T(\d{2})/;
const RE_SEMANA = /^(\d{4})-W(\d{1,2})$/i;
const RE_DIA = /^\d{4}-\d{2}-\d{2}$/;
const RE_MES = /^\d{4}-\d{2}$/;
const RE_ANIO = /^\d{4}$/;

function formatoDeBucket(bucket: string): FormatoDeBucket | null {
  if (RE_HORA.test(bucket)) return 'hora';
  if (RE_SEMANA.test(bucket)) return 'semana';
  if (RE_DIA.test(bucket)) return 'dia';
  if (RE_MES.test(bucket)) return 'mes';
  if (RE_ANIO.test(bucket)) return 'anio';
  return null;
}

/** Semana ISO (`YYYY-Www`, con cero a la izquierda) de una fecha `YYYY-MM-DD`. */
function semanaIso(dia: string): string {
  const [y, m, d] = dia.split('-').map(Number);
  const jueves = new Date(y, m - 1, d);
  jueves.setDate(jueves.getDate() - ((jueves.getDay() + 6) % 7) + 3);
  const anioIso = jueves.getFullYear();
  const primerJueves = new Date(anioIso, 0, 4);
  primerJueves.setDate(primerJueves.getDate() - ((primerJueves.getDay() + 6) % 7) + 3);
  const semana = 1 + Math.round((jueves.getTime() - primerJueves.getTime()) / (7 * 86400000));
  return `${anioIso}-W${String(semana).padStart(2, '0')}`;
}

/**
 * Lleva un bucket al formato del eje. Normaliza la forma (hora → `YYYY-MM-DDTHH:00`,
 * semana con dos dígitos) para que la unión no duplique la misma hora/semana escrita
 * distinto y el orden lexicográfico sea el cronológico. Solo se puede ENGROSAR (hora →
 * día → mes; hora/día → semana ISO): afinar no se puede (un mes no se reparte en días)
 * y semana → mes tampoco (una semana cruza meses). Sin conversión posible → `null`.
 */
function llevarAlEje(bucket: string, origen: FormatoDeBucket, eje: FormatoDeBucket): string | null {
  const hora = RE_HORA.exec(bucket);
  const semana = RE_SEMANA.exec(bucket);
  const dia = hora ? hora[1] : origen === 'dia' ? bucket : null;
  if (origen === eje) {
    if (hora) return `${hora[1]}T${hora[2]}:00`;
    if (semana) return `${semana[1]}-W${semana[2].padStart(2, '0')}`;
    return bucket;
  }
  switch (eje) {
    case 'dia':
      return hora ? hora[1] : null;
    case 'semana':
      return dia ? semanaIso(dia) : null;
    case 'mes':
      return dia ? dia.slice(0, 7) : null;
    default:
      // 'anio' (no se usa en "Todo") y 'hora' (no se puede afinar).
      return null;
  }
}

/**
 * Alinea TODAS las series de "Todo" (SPEI/Cards, TPV y pagos) sobre UN eje común: la
 * unión ordenada de sus buckets en el formato del eje, con 0 donde a una serie le falte
 * un bucket (R33). La línea de "Todo" (`global-line-chart`) toma el eje de la serie más
 * larga y pinta las demás por POSICIÓN, así que sin esto una serie con buckets propios
 * (TPV, o pagos en `año`, que solo trae meses con cobros) se dibujaba en meses ajenos.
 *
 * Formato del eje: el de la PRIMERA serie con puntos — global-summary va primero y es la
 * referencia del periodo; sin ella, la siguiente (TPV o pagos).
 *
 * Una serie que no se puede llevar al formato del eje (p. ej. TPV mensual sobre un eje
 * por hora o por día) se QUEDA FUERA de la línea: meterla por índice la pintaría en horas
 * que no son las suyas. Sigue contando en totales, tarjetas, barras y dona — esos no
 * dependen del eje. Si es más fina que el eje (TPV por día sobre un eje mensual) se
 * re-agrupa sumando.
 */
export function alinearSeriesAlEje(series: readonly GlobalServiceSeries[]): GlobalServiceSeries[] {
  let formatoEje: FormatoDeBucket | null = null;
  for (const s of series) {
    for (const p of s.points) {
      formatoEje = formatoDeBucket(p.bucket);
      if (formatoEje) break;
    }
    if (formatoEje) break;
  }
  if (!formatoEje) return [...series];
  const eje = formatoEje;

  const convertidas = series
    .map((s) => {
      const valores = new Map<string, number>();
      for (const p of s.points) {
        const origen = formatoDeBucket(p.bucket);
        const destino = origen ? llevarAlEje(p.bucket, origen, eje) : null;
        if (destino === null) return null; // no cabe en el eje: fuera de la línea
        valores.set(destino, (valores.get(destino) ?? 0) + (Number(p.value) || 0));
      }
      return { serviceId: s.serviceId, valores };
    })
    .filter((c): c is { serviceId: GlobalServiceId; valores: Map<string, number> } => c !== null);

  const buckets = [...new Set(convertidas.flatMap((c) => [...c.valores.keys()]))].sort();
  return convertidas.map((c) => ({
    serviceId: c.serviceId,
    points: buckets.map((bucket) => ({ bucket, value: c.valores.get(bucket) ?? 0 })),
  }));
}

/** Recalcula los porcentajes de la dona sobre el total combinado (mismo redondeo de siempre). */
function conPorcentajesDeUso(slices: ServiceUsageSlice[]): ServiceUsageSlice[] {
  const total = slices.reduce((sum, s) => sum + s.count, 0);
  return slices.map((s) => ({
    ...s,
    percentage: total > 0 ? Math.round((s.count * 1000) / total) / 10 : 0,
  }));
}

/**
 * Fusiona el resumen de TPV (endpoint `api/v1/tpv/dashboard/`, que ya scopea
 * por comercios) dentro del resumen global de SPEI+CARDS. El backend global NO
 * agrega TPV a propósito — ver GlobalDashboardRemoteDataSource.
 *
 * Semántica TPV → global: ventas = ingresos; cancelaciones = egresos.
 * Los porcentajes de la dona se recalculan sobre el total combinado, y la serie se
 * alinea POR BUCKET con las demás (`alinearSeriesAlEje`), ya no por posición.
 */
export function mergeTpvIntoGlobalSummary(
  summary: GlobalDashboardSummary,
  tpv: {
    totalSales: { count: number; amount: number };
    cancellations: { count: number; amount: number };
    transactionsTotal: number;
    monthlyTransactions: Array<{ month: string; value: number }>;
  }
): GlobalDashboardSummary {
  return {
    ...summary,
    availableServices: [...summary.availableServices, 'tpv'],
    lineCards: [
      ...summary.lineCards,
      {
        lineId: 'tpv',
        ingresosCount: tpv.totalSales.count,
        ingresosAmount: tpv.totalSales.amount,
        egresosCount: tpv.cancellations.count,
        egresosAmount: tpv.cancellations.amount,
        currency: summary.currency,
      },
    ],
    series: alinearSeriesAlEje([
      ...summary.series,
      {
        serviceId: 'tpv',
        points: (tpv.monthlyTransactions ?? []).map((p) => ({
          bucket: p.month,
          value: Number(p.value) || 0,
        })),
      },
    ]),
    incomeExpense: [
      ...summary.incomeExpense,
      {
        serviceId: 'tpv',
        ingresos: tpv.totalSales.amount,
        egresos: tpv.cancellations.amount,
      },
    ],
    serviceUsage: conPorcentajesDeUso([
      ...summary.serviceUsage,
      { serviceId: 'tpv', count: tpv.transactionsTotal ?? 0, percentage: 0 },
    ]),
  };
}

/** Operaciones cobradas de un bucket: links + cargos + QR. */
function cobradasDe(p: PaymentsSeriePoint): number {
  return p.link.count + p.charge.count + p.qr.count;
}

/**
 * Servicios que el back podría mandar en global-summary y que la Pasarela de Pagos YA
 * cuenta: "payments" del front = links + cargos + QR de `/payments/dashboard/`. Si el back
 * algún día incluye `payment_links` o `polipay_qr`, sumar los dos sería contar el mismo
 * dinero dos veces; al fusionar pagos se DESCARTAN esas entradas (series, barras, dona y
 * servicios disponibles). Se prefiere la cifra del front porque es la que pinta la vista
 * de pagos con las mismas reglas (montos "sin dato", solo MXN).
 */
const CUBIERTOS_POR_PAGOS: ReadonlySet<GlobalServiceId> = new Set<GlobalServiceId>(['payment_links', 'polipay_qr']);

/**
 * Fusiona la Pasarela de Pagos (`/api/v1/payments/dashboard/`, que el back ya
 * recorta por los gestores del JWT) en el resumen global, como un servicio y una
 * línea más:
 *  - serie por bucket = operaciones cobradas (links + cargos + QR), alineada por bucket
 *    con las demás series (`alinearSeriesAlEje`, R33);
 *  - ingresos = suma de los montos cobrados CON dato; si TODOS son "sin dato", el
 *    ingreso queda `null` y "Todo" pinta "—" como la vista de pagos (R24) — un 0 diría
 *    que no se cobró nada; sin buckets es un cero verdadero. Egresos = 0 (la pasarela
 *    no tiene salidas en este contrato);
 *  - uso del servicio = total de operaciones cobradas, y la dona se recalcula;
 *  - se descartan `payment_links`/`polipay_qr` del back (ver `CUBIERTOS_POR_PAGOS`).
 */
export function mergePaymentsIntoGlobalSummary(
  summary: GlobalDashboardSummary,
  pagos: PaymentsDashboardData,
): GlobalDashboardSummary {
  const conteo = pagos.serie.reduce((suma, p) => suma + cobradasDe(p), 0);
  const montos = pagos.serie.flatMap((p) => [p.link.amount, p.charge.amount, p.qr.amount]);
  const ingresos = montos.length === 0 ? 0 : sumarMontos(montos);
  const fuera = <T extends { serviceId: GlobalServiceId }>(items: readonly T[]) =>
    items.filter((i) => !CUBIERTOS_POR_PAGOS.has(i.serviceId));

  return {
    ...summary,
    availableServices: [...summary.availableServices.filter((s) => !CUBIERTOS_POR_PAGOS.has(s)), 'payments'],
    lineCards: [
      ...summary.lineCards,
      {
        lineId: 'payments',
        ingresosCount: conteo,
        ingresosAmount: ingresos,
        egresosCount: 0,
        egresosAmount: 0,
        currency: summary.currency,
      },
    ],
    series: alinearSeriesAlEje([
      ...fuera(summary.series),
      {
        serviceId: 'payments',
        points: pagos.serie.map((p) => ({ bucket: p.bucket, value: cobradasDe(p) })),
      },
    ]),
    incomeExpense: [...fuera(summary.incomeExpense), { serviceId: 'payments', ingresos, egresos: 0 }],
    serviceUsage: conPorcentajesDeUso([
      ...fuera(summary.serviceUsage),
      { serviceId: 'payments', count: conteo, percentage: 0 },
    ]),
  };
}

export function mapGlobalDashboardSummary(dto: GlobalDashboardSummaryDto): GlobalDashboardSummary {
  const currency = dto?.currency || DEFAULT_CURRENCY;
  const availableServices = (dto?.available_services ?? [])
    .map(toServiceId)
    .filter((s): s is GlobalServiceId => s !== null);

  const granularity = dto?.series_granularity;
  return {
    periodLabel: dto?.period_label ?? '',
    // `global_balance` (legado) NO se lee: con total ausente banca manda "0".
    balance: mapGlobalBalance(dto?.balance),
    currency,
    availableServices,
    seriesGranularity:
      granularity === 'hour' || granularity === 'day' || granularity === 'month'
        ? granularity
        : 'month',
    lineCards: mapLineCards(dto?.business_lines, currency),
    series: mapSeries(dto?.series),
    incomeExpense: mapIncomeExpense(dto?.income_expense),
    serviceUsage: mapServiceUsage(dto?.service_usage),
    recentActivity: mapRecentActivity(dto?.recent_activity, currency),
  };
}
